import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { RerunApplicabilityCandidate } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/shared/lib/utils'

export type RerunCandidateReviewProps = {
  candidates: RerunApplicabilityCandidate[]
  selectedReferences: Set<string>
  onSelectedReferencesChange: (selected: Set<string>) => void
  onContinue: () => void
  continueLabel: string
  emptyMessage: string
  isBusy?: boolean
}

export function RerunCandidateReview({
  candidates,
  selectedReferences,
  onSelectedReferencesChange,
  onContinue,
  continueLabel,
  emptyMessage,
  isBusy = false,
}: RerunCandidateReviewProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const sortedCandidates = useMemo(
    () =>
      [...candidates].sort((first, second) =>
        first.reference.localeCompare(second.reference)
      ),
    [candidates]
  )

  const toggleReference = useCallback(
    (reference: string, checked: boolean) => {
      const next = new Set(selectedReferences)
      if (checked) {
        next.add(reference)
      } else {
        next.delete(reference)
      }
      onSelectedReferencesChange(next)
    },
    [onSelectedReferencesChange, selectedReferences]
  )

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        onSelectedReferencesChange(
          new Set(sortedCandidates.map((candidate) => candidate.reference))
        )
        return
      }
      onSelectedReferencesChange(new Set())
    },
    [onSelectedReferencesChange, sortedCandidates]
  )

  const allSelected =
    sortedCandidates.length > 0 &&
    sortedCandidates.every((candidate) =>
      selectedReferences.has(candidate.reference)
    )

  if (sortedCandidates.length === 0) {
    return (
      <div className='space-y-4'>
        <p className='text-sm text-muted-foreground'>{emptyMessage}</p>
        <div className='flex justify-end'>
          <Button type='button' onClick={onContinue} disabled={isBusy}>
            {continueLabel}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='flex items-center justify-between gap-2'>
        <p className='text-sm text-muted-foreground'>
          {t('standardsRerun.review.selectedCount', {
            selected: selectedReferences.size,
            total: sortedCandidates.length,
          })}
        </p>
        <label className='flex items-center gap-2 text-sm'>
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => toggleAll(checked === true)}
            disabled={isBusy}
          />
          {t('standardsRerun.review.selectAll')}
        </label>
      </div>
      <div className='min-h-0 flex-1 space-y-2 overflow-y-auto pr-1'>
        {sortedCandidates.map((candidate) => {
          const isSelected = selectedReferences.has(candidate.reference)
          return (
            <div
              key={candidate.reference}
              className={cn(
                'flex gap-3 rounded-md border p-3',
                isSelected && 'border-primary/40 bg-muted/30'
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) =>
                  toggleReference(candidate.reference, checked === true)
                }
                disabled={isBusy}
                className='mt-0.5'
              />
              <div className='min-w-0 flex-1 space-y-1'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='font-medium'>{candidate.reference}</span>
                  <Badge variant='outline'>{candidate.type}</Badge>
                </div>
                <p className='text-sm text-foreground'>{candidate.title}</p>
                <p className='text-xs text-muted-foreground'>
                  {candidate.explanation}
                </p>
              </div>
            </div>
          )
        })}
      </div>
      <div className='flex justify-end border-t pt-3'>
        <Button type='button' onClick={onContinue} disabled={isBusy}>
          {continueLabel}
        </Button>
      </div>
    </div>
  )
}
